import axios from 'axios';
import type {
  Counterparty,
  CounterpartyTeam,
  WTRequest,
  LoginRequest,
  LoginResponse,
  DashboardStatsResponse,
  VndQrProfile,
  VndQrProfilePayload,
  VndQrAutoProfileRequestPayload,
  VndQrAutoProfileRequest,
  VndCounterpartyRequest,
  VNDPayoutRequest,
  VNDTierRateRow,
} from '../types';

export type CpSmsProfileStatus = {
  enabled: boolean;
  listen_mode?: 'sms' | 'notification' | '';
  connection?: string;
  phone?: { id?: string; label?: string } | null;
  device?: { locked?: boolean | null; screen_on?: boolean | null; push_shade_count?: number | null } | null;
};

export type CpSmsProfileEvent = {
  id: number;
  created_at?: string;
  amount_vnd?: number;
  status?: string;
  confirm_status?: string;
};

export type CpSmsPhoneProfile = {
  source: 'qr' | 'bank';
  id: number;
  name?: string;
  bank_name?: string;
  account_no?: string;
  listen_on?: boolean;
  profile_active?: boolean;
  payment_provider?: 'sepay' | 'casso' | string;
  work_mode?: string;
};

export type CpSmsPhone = {
  id: string;
  label?: string;
  enabled?: boolean;
  has_key?: boolean;
  connection?: string;
  last_seen_at?: string | null;
  battery?: number | null;
  app_version?: string;
  device_label?: string;
  bind_key?: string;
  managed?: boolean;
  idle_paused?: boolean;
  locked?: boolean | null;
  screen_on?: boolean | null;
  profiles?: CpSmsPhoneProfile[];
};

export type CpSmsAttachCandidate = {
  source: 'qr' | 'bank';
  id: number;
  name?: string;
  bank_name?: string;
  account_no?: string;
  payment_provider?: 'sepay' | 'casso' | string;
  work_mode?: string;
};

export type CpProfileFieldAuditItem = {
  id: number;
  field: string;
  old_value?: string | null;
  new_value?: string | null;
  actor?: string;
  created_at?: string;
};

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('airex_cp_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Currency'] = 'VND';
  // Для FormData не задаём Content-Type — axios сам выставит multipart/form-data с boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    if (status === 403 && data?.error === 'cabinet_suspended') {
      localStorage.removeItem('airex_cp_token');
      const msg = (data?.message || 'Кабинет заблокирован.').trim();
      window.alert(msg);
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }
    if (status === 401 && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('airex_cp_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const cpApi = {
  login: async (creds: LoginRequest): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/counterparty/login', { ...creds, platform: 'regular' });
    return data;
  },

  loginWith2FA: async (login: string, code: string, password: string): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/counterparty/login/2fa', {
      login,
      code,
      password,
      platform: 'regular',
    });
    return data;
  },

  getProfile: async (): Promise<Counterparty> => {
    const { data } = await api.get<{ data: Counterparty }>('/counterparty/profile');
    return data.data || data;
  },

  updateProfile: async (payload: { login?: string; password?: string; name?: string }) => {
    await api.put('/counterparty/profile', payload);
  },

  updateTeamMemberProfile: async (counterpartyId: number, payload: { login?: string; password?: string; show_rates_in_sidebar?: boolean }) => {
    await api.put(`/counterparty/team-members/${counterpartyId}/profile`, payload);
  },

  getMyTeam: async (): Promise<CounterpartyTeam | null> => {
    const { data } = await api.get<{ data: CounterpartyTeam | null }>('/counterparty/my-team');
    return data.data ?? null;
  },

  addMyTeamMember: async (payload: { name: string; login: string; password: string }) => {
    const { data } = await api.post<{ data: unknown }>('/counterparty/my-team/members', payload);
    return data;
  },

  removeMyTeamMember: async (counterpartyId: number) => {
    await api.delete(`/counterparty/my-team/members/${counterpartyId}`);
  },

  getSalaryBalance: async (): Promise<number> => {
    const { data } = await api.get<{ data?: { salary_balance?: number } }>('/counterparty/salary-balance');
    return data?.data?.salary_balance ?? 0;
  },

  getDashboardStats: async (params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const { data } = await api.get<DashboardStatsResponse>(`/counterparty/dashboard-stats${q.toString() ? `?${q}` : ''}`);
    return data;
  },

  getDashboardWorkloadSeries: async (
    params: {
      from: number;
      to: number;
      tf: import('./dashboard').WorkloadTf;
      profileKeys?: string[];
      includeProfiles?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<import('./dashboard').WorkloadSeriesResponse> => {
    const q = new URLSearchParams({
      from: String(Math.floor(params.from)),
      to: String(Math.floor(params.to)),
      tf: params.tf,
    });
    if (params.profileKeys && params.profileKeys.length > 0) {
      q.set('profile_keys', params.profileKeys.join(','));
    }
    if (params.includeProfiles) q.set('include_profiles', '1');
    const { data } = await api.get(`/counterparty/dashboard/workload-series?${q.toString()}`, { signal });
    return data;
  },

  getDashboardCharts: async (params: {
    dateFrom: string;
    dateTo: string;
    granularity?: import('./dashboard').DashboardGranularity;
    profileIds?: number[];
  }): Promise<import('./dashboard').CpDashboardChartsResponse> => {
    const q = new URLSearchParams({
      date_from: params.dateFrom,
      date_to: params.dateTo,
    });
    if (params.granularity) q.set('granularity', params.granularity);
    if (params.profileIds && params.profileIds.length > 0) {
      q.set('profile_ids', params.profileIds.join(','));
    }
    const { data } = await api.get(`/counterparty/dashboard/charts?${q.toString()}`);
    return data;
  },

  getRequests: async (params?: { status?: string; date_from?: string; date_to?: string }): Promise<WTRequest[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/counterparty/requests${q.toString() ? `?${q}` : ''}`);
    const arr = data.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  /**
   * Заявки в активных статусах для бейджа сайдбара: «в работе» + «к закрытию».
   * Используется поле total от API (учёт всех страниц пагинации), широкий диапазон дат — без обрезки по календарю.
   */
  getVndSidebarActiveRequestsTotal: async (): Promise<number> => {
    const base = {
      date_from: '1970-01-01',
      date_to: '2099-12-31',
      page: 1,
      limit: 1,
    } as const;
    const [work, pendingCanceled] = await Promise.all([
      cpApi.getVndRequests({ ...base, status: 'work' }),
      cpApi.getVndRequests({ ...base, status: 'pending_canceled' }),
    ]);
    return (work.total || 0) + (pendingCanceled.total || 0);
  },

  getVndSidebarPayoutCounts: async (): Promise<{ available: number; active: number }> => {
    const base = {
      date_from: '1970-01-01',
      date_to: '2099-12-31',
      page: 1,
      limit: 1,
    } as const;
    const [open, work] = await Promise.all([
      cpApi.getVndPayoutRequests({ ...base, status: 'open' }),
      cpApi.getVndPayoutRequests({ ...base, status: 'work' }),
    ]);
    return {
      available: Number.isFinite(open.total) ? open.total : 0,
      active: Number.isFinite(work.total) ? work.total : 0,
    };
  },

  getVndRequests: async (params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    profile_id?: number;
    page?: number;
    limit?: number;
  }): Promise<{
    items: VndCounterpartyRequest[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  }> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    if (params?.search) q.set('search', params.search.trim());
    if (params?.profile_id != null && params.profile_id > 0) q.set('profile_id', String(params.profile_id));
    if (params?.page != null && params.page > 0) q.set('page', String(params.page));
    if (params?.limit != null && params.limit >= 0) q.set('limit', String(params.limit));
    const { data } = await api.get(`/counterparty/requests/vnd${q.toString() ? `?${q}` : ''}`);
    const items = Array.isArray(data.data) ? data.data : [];
    return {
      items,
      total: typeof data.total === 'number' ? data.total : items.length,
      page: typeof data.page === 'number' ? data.page : 1,
      limit: typeof data.limit === 'number' ? data.limit : 0,
      total_pages: typeof data.total_pages === 'number' ? data.total_pages : 1,
    };
  },

  approveVndRequest: async (id: number) => api.post(`/counterparty/requests/${id}/confirm`),
  cancelVndRequest: async (id: number, comment?: string) =>
    api.post(`/counterparty/requests/${id}/reject`, { comment: comment || '' }),

  getVndQrProfiles: async (params?: { include_deleted?: boolean }): Promise<VndQrProfile[]> => {
    const q = new URLSearchParams();
    if (params?.include_deleted) q.set('include_deleted', 'true');
    const { data } = await api.get(`/counterparty/vnd-qr/profiles${q.toString() ? `?${q}` : ''}`);
    const arr = data.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  createVndQrProfile: async (payload: VndQrProfilePayload): Promise<VndQrProfile> => {
    const { data } = await api.post('/counterparty/vnd-qr/profiles', payload);
    return data.data ?? data;
  },

  createVndQrAutoProfileRequest: async (payload: VndQrAutoProfileRequestPayload) => {
    const { data } = await api.post('/counterparty/vnd-qr/auto-profile-requests', payload);
    return data.data ?? data;
  },

  getVndQrAutoProfileRequests: async (params?: { status?: string }): Promise<VndQrAutoProfileRequest[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const { data } = await api.get(`/counterparty/vnd-qr/auto-profile-requests${suffix}`);
    const arr = data?.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  updateVndQrAutoProfileRequest: async (id: number, payload: VndQrAutoProfileRequestPayload) => {
    const { data } = await api.put(`/counterparty/vnd-qr/auto-profile-requests/${id}`, payload);
    return data.data ?? data;
  },

  updateVndQrProfile: async (id: number, payload: Partial<VndQrProfilePayload>): Promise<VndQrProfile> => {
    const { data } = await api.put(`/counterparty/vnd-qr/profiles/${id}`, payload);
    return data.data ?? data;
  },

  deleteVndQrProfile: async (id: number) => {
    await api.delete(`/counterparty/vnd-qr/profiles/${id}`);
  },

  restoreVndQrProfile: async (id: number) => {
    await api.post(`/counterparty/vnd-qr/profiles/${id}/restore`);
  },

  getVndQrProfileSms: async (id: number): Promise<CpSmsProfileStatus> => {
    const { data } = await api.get(`/counterparty/vnd-qr/profiles/${id}/sms`);
    return data;
  },

  putVndQrProfileSms: async (
    id: number,
    body: { enabled?: boolean; listen_mode?: 'sms' | 'notification' },
  ): Promise<CpSmsProfileStatus> => {
    const { data } = await api.put(`/counterparty/vnd-qr/profiles/${id}/sms`, body);
    return data;
  },

  getVndQrProfileSmsEvents: async (
    id: number,
    params: { page: number; limit: number; date_from?: string; date_to?: string },
  ) => {
    const q = new URLSearchParams();
    q.set('page', String(params.page));
    q.set('limit', String(params.limit));
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/counterparty/vnd-qr/profiles/${id}/sms/events?${q.toString()}`);
    return data as { events?: CpSmsProfileEvent[]; total?: number; page?: number; limit?: number; total_pages?: number };
  },

  getVndQrProfileAudit: async (id: number, params: { page: number; limit: number }) => {
    const q = new URLSearchParams();
    q.set('page', String(params.page));
    q.set('limit', String(params.limit));
    const { data } = await api.get(`/counterparty/vnd-qr/profiles/${id}/audit?${q.toString()}`);
    return data as {
      items?: CpProfileFieldAuditItem[];
      total?: number;
      page?: number;
      limit?: number;
      total_pages?: number;
    };
  },

  listSmsPhones: async (): Promise<{ phones: CpSmsPhone[]; candidates: CpSmsAttachCandidate[] }> => {
    const { data } = await api.get('/counterparty/sms/phones');
    return {
      phones: Array.isArray(data?.phones) ? data.phones : [],
      candidates: Array.isArray(data?.candidates) ? data.candidates : [],
    };
  },

  createSmsPhone: async (body?: { label?: string }): Promise<CpSmsPhone> => {
    const { data } = await api.post('/counterparty/sms/phones', body ?? {});
    return data;
  },

  putSmsPhone: async (
    id: string,
    body: { enabled?: boolean; label?: string },
  ): Promise<CpSmsPhone> => {
    const { data } = await api.put(`/counterparty/sms/phones/${id}`, body);
    return data;
  },

  deleteSmsPhone: async (id: string) => {
    await api.delete(`/counterparty/sms/phones/${id}`);
  },

  rotateSmsPhoneKey: async (id: string): Promise<CpSmsPhone> => {
    const { data } = await api.post(`/counterparty/sms/phones/${id}/rotate-key`, {});
    return data;
  },

  attachSmsPhoneProfile: async (id: string, profileId: number): Promise<CpSmsPhone> => {
    const { data } = await api.post(`/counterparty/sms/phones/${id}/profiles`, { source: 'qr', profile_id: profileId });
    return data;
  },

  detachSmsPhoneProfile: async (id: string, profileId: number): Promise<CpSmsPhone> => {
    const { data } = await api.delete(`/counterparty/sms/phones/${id}/profiles/qr/${profileId}`);
    return data;
  },

  uploadVndQrFile: async (id: number, file: File): Promise<VndQrProfile> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/counterparty/vnd-qr/profiles/${id}/file`, formData);
    return data.data ?? data;
  },

  /** Файл QR защищён JWT — для превью и скачивания только через blob. */
  getVndQrProfileFileBlob: async (id: number): Promise<Blob> => {
    const { data } = await api.get<Blob>(`/counterparty/vnd-qr/profiles/${id}/file`, {
      responseType: 'blob',
    });
    return data;
  },

  getVndQrProfileSepayTransactions: async (
    id: number,
    params: { limit?: number; page?: number; date_from?: string; date_to?: string }
  ) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.page) q.set('page', String(params.page));
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/counterparty/vnd-qr/profiles/${id}/sepay-transactions${q.toString() ? `?${q}` : ''}`);
    return data;
  },

  getVndTeam: async (): Promise<{ team: { id: number; name: string; responsible_counterparty_id: number }; members: Array<{ counterparty_id: number; name: string; login: string; salary_balance: number }> }> => {
    const { data } = await api.get('/counterparty/team');
    return data.data ?? data;
  },

  /** Градации VND/USDT для команды текущего контрагента */
  getVndTeamTiers: async (): Promise<{ team_id: number; base_vnd_per_usdt: number; tiers: VNDTierRateRow[] }> => {
    const { data } = await api.get('/counterparty/vnd-team/tiers');
    const inner = (data as { data?: { team_id?: number; base_vnd_per_usdt?: number; tiers?: VNDTierRateRow[] } })?.data ?? data;
    return {
      team_id: typeof inner?.team_id === 'number' ? inner.team_id : 0,
      base_vnd_per_usdt: typeof inner?.base_vnd_per_usdt === 'number' ? inner.base_vnd_per_usdt : 0,
      tiers: Array.isArray(inner?.tiers) ? inner.tiers : [],
    };
  },

  getVndTeamPayoutTiers: async (): Promise<{ team_id: number; tiers: Array<{ id?: number; sort_order?: number; min_vnd: number; max_vnd: number; rate_percent: number }> }> => {
    const { data } = await api.get('/counterparty/vnd-team/payout-tiers');
    const inner = (data as { data?: { team_id?: number; tiers?: Array<{ id?: number; sort_order?: number; min_vnd: number; max_vnd: number; rate_percent: number }> } })?.data ?? data;
    return {
      team_id: typeof inner?.team_id === 'number' ? inner.team_id : 0,
      tiers: Array.isArray(inner?.tiers) ? inner.tiers : [],
    };
  },

  getVndPayoutRequests: async (params?: { status?: string; date_from?: string; date_to?: string; search?: string; page?: number; limit?: number }): Promise<{
    items: VNDPayoutRequest[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  }> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    if (params?.search) q.set('search', params.search.trim());
    if (params?.page != null && params.page > 0) q.set('page', String(params.page));
    if (params?.limit != null && params.limit >= 0) q.set('limit', String(params.limit));
    const { data } = await api.get(`/counterparty/vnd-payout-requests${q.toString() ? `?${q}` : ''}`);
    const items = Array.isArray(data?.data) ? data.data : [];
    return {
      items,
      total: typeof data?.total === 'number' ? data.total : items.length,
      page: typeof data?.page === 'number' ? data.page : 1,
      limit: typeof data?.limit === 'number' ? data.limit : 0,
      total_pages: typeof data?.total_pages === 'number' ? data.total_pages : 1,
    };
  },

  takeVndPayoutRequest: async (id: number): Promise<void> => {
    await api.post(`/counterparty/vnd-payout-requests/${id}/take`);
  },

  completeVndPayoutRequest: async (id: number): Promise<void> => {
    await api.post(`/counterparty/vnd-payout-requests/${id}/complete`);
  },

  addVndTeamMember: async (payload: { name: string; login: string; password: string }) => {
    const { data } = await api.post('/counterparty/team/members', payload);
    return data.data ?? data;
  },

  updateVndTeamMember: async (
    memberId: number,
    payload: { name?: string; login?: string; password?: string }
  ): Promise<void> => {
    await api.put(`/counterparty/team/members/${memberId}`, payload);
  },

  removeVndTeamMember: async (memberId: number): Promise<void> => {
    await api.delete(`/counterparty/team/members/${memberId}`);
  },

  getVndBalanceHistory: async (params?: {
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
    member_id?: number;
    type?: string;
    search?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    if (params?.member_id != null && params.member_id > 0) q.set('member_id', String(params.member_id));
    if (params?.type && params.type !== 'all') q.set('type', params.type);
    if (params?.search?.trim()) q.set('search', params.search.trim());
    const { data } = await api.get(`/counterparty/balance-history${q.toString() ? `?${q.toString()}` : ''}`);
    return {
      data: Array.isArray(data.data) ? data.data : [],
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? params?.limit ?? 50),
      offset: Number(data.offset ?? params?.offset ?? 0),
    };
  },

  getAvailableAirexRequests: async (params?: { amount?: number; tolerance_percent?: number; limit?: number }): Promise<unknown[]> => {
    const q = new URLSearchParams();
    if (params?.amount != null && params.amount > 0) q.set('amount', String(params.amount));
    if (params?.tolerance_percent != null && params.tolerance_percent > 0) q.set('tolerance_percent', String(params.tolerance_percent));
    if (params?.limit != null && params.limit > 0) q.set('limit', String(params.limit));
    const url = `/airex/requests/available${q.toString() ? `?${q}` : ''}`;
    const { data } = await api.get(url, { timeout: 30000 });
    const arr = data.data ?? data;
    return Array.isArray(arr) ? arr : [];
  },

  getMyAirexRequests: async (params?: { dateFrom?: string; dateTo?: string; status?: string; search?: string; limit?: number; offset?: number }): Promise<{ data: unknown[]; total_count: number }> => {
    const q = new URLSearchParams();
    if (params?.dateFrom) q.set('date_from', params.dateFrom);
    if (params?.dateTo) q.set('date_to', params.dateTo);
    if (params?.status) q.set('status', params.status);
    if (params?.search?.trim()) q.set('search', params.search.trim());
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const { data } = await api.get(`/airex/requests/my${q.toString() ? `?${q}` : ''}`, { timeout: 30000 });
    return { data: Array.isArray(data.data) ? data.data : [], total_count: data.total_count ?? 0 };
  },

  getAirexRequestMatches: async (requestId: number) => {
    const { data } = await api.get(`/airex/requests/${requestId}/matches`);
    return (data?.data?.list ?? data?.list ?? []) as unknown[];
  },

  getMatchSettings: async () => {
    const { data } = await api.get('/airex-settings/match-settings');
    return data;
  },

  getWallets: async (params?: { limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const { data } = await api.get(`/wallets${q.toString() ? `?${q}` : ''}`);
    return { wallets: data.data ?? [], total: data.total ?? 0 };
  },

  createManualMatch: async (requestId: number, formData: FormData) => {
    const { data } = await api.post(`/airex/requests/${requestId}/manual-match`, formData, {
      timeout: 60000,
    });
    return data;
  },

  pickRequest: async (params: { amount: number; direction_code?: string; timeout_seconds?: number }) => {
    const timeout = Math.max(0, Math.min(300, params.timeout_seconds ?? 0));
    // минимум 120 сек — при 200–300 заявках обработка занимает значительное время
    const waitMs = Math.max((timeout + 60) * 1000, 120000);
    const { data } = await api.post(
      '/airex/requests/pick',
      {
        amount: params.amount,
        direction_code: params.direction_code || '',
        timeout_seconds: timeout,
      },
      { timeout: waitMs }
    );
    return data;
  },

  getWTRequest: async (id: number) => {
    const { data } = await api.get(`/wt-requests/${id}`);
    return data?.data ?? data;
  },

  downloadReceipt: async (wtRequestId: number): Promise<Blob> => {
    const { data } = await api.get(`/wt-requests/${wtRequestId}/download-receipt`, { responseType: 'blob' });
    return data as Blob;
  },

  updateReceiptCheckFlags: async (requestId: number, flags: { check_amount?: boolean; check_recipient?: boolean; check_card?: boolean; check_bank?: boolean; check_status?: boolean }) => {
    await api.post(`/wt-requests/${requestId}/receipt-check-flags`, flags);
  },

  resetReceiptOriginality: async (requestId: number) => {
    await api.post(`/wt-requests/${requestId}/receipt-originality/reset`);
  },

  replaceReceipt: async (requestId: number, file: File): Promise<{ file_path: string }> => {
    const formData = new FormData();
    formData.append('receipt', file);
    const { data } = await api.post<{ file_path: string }>(`/wt-requests/${requestId}/replace-receipt`, formData);
    return data;
  },

  takeRequest: async (id: number) => api.post(`/counterparty/requests/${id}/take`, undefined, { timeout: 45000 }),
  takeAirexRequest: async (id: number) => api.post(`/airex/requests/${id}/take`, undefined, { timeout: 45000 }),
  confirmRequest: async (id: number) => api.post(`/counterparty/requests/${id}/confirm`),
  rejectRequest: async (id: number, comment?: string) => api.post(`/counterparty/requests/${id}/reject`, { comment: comment || '' }),
  /** Отменить WT-заявку и мэтч (требует can_cancel у контрагента), rejection_method=contr */
  rejectWTRequest: async (wtRequestId: number) => api.post(`/wt-requests/${wtRequestId}/reject?method=contr`),

  updateActivity: async () => api.post('/counterparty/activity'),

  getMyCurrentRates: async () => {
    const { data } = await api.get('/counterparty/current-rates');
    return data as {
      calculated_commissions: { min: number; max: number; rate: number }[];
      base_rate?: number;
      commissions?: { min: number; max: number; percent: number }[];
    };
  },

  getTransactions: async (params?: { limit?: number; offset?: number; to_entity_type?: string; to_entity_id?: number; date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.to_entity_type) q.set('to_entity_type', params.to_entity_type);
    if (params?.to_entity_id != null) q.set('to_entity_id', String(params.to_entity_id));
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/transactions${q.toString() ? `?${q}` : ''}`);
    return { transactions: data.data ?? [], total: data.total ?? 0 };
  },

  transferTeamInternal: async (payload: { from_counterparty_id: number; to_counterparty_id: number; amount: number; comment?: string }) => {
    const { data } = await api.post('/counterparty/team-transfer', payload);
    return data;
  },

  createWithdrawTask: async (payload: { amount: number; network: 'HTX_UID' | 'BEP20' | 'TRC20' | 'TON' | 'ERC20'; wallet?: string; htx_uid?: string; comment?: string }) => {
    const { data } = await api.post('/counterparty/withdraw-tasks', payload);
    return data;
  },

  getTeamTransactions: async (params?: { limit?: number; offset?: number; type?: string; member_id?: number; date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.type) q.set('type', params.type);
    if (params?.member_id != null) q.set('member_id', String(params.member_id));
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    const { data } = await api.get(`/counterparty/team-transactions${q.toString() ? `?${q}` : ''}`);
    return { transactions: data.data ?? [], total: data.total ?? 0 };
  },

  setup2FA: async () => (await api.post('/counterparty/2fa/setup')).data,
  enable2FA: async (code: string) => (await api.post('/counterparty/2fa/enable', { code })).data,
  disable2FA: async (code: string) => (await api.post('/counterparty/2fa/disable', { code })).data,
  get2FAQRCode: async () => (await api.get('/counterparty/2fa/qr-code')).data,
  getBackupCodes: async () => (await api.post('/counterparty/2fa/backup-codes')).data,
};

export default api;
